import { toast } from 'sonner'

/**
 * Confirmation for a manual row added to an Analysis table.
 *
 * These tables carry persistent, deep-linkable filters, and a manual row is
 * created with default values (`not_assessed`, `desirable`, no coverage) that a
 * narrowed table usually excludes. Without a confirmation the dialog just
 * closes over an unchanged table, which reads as a failed save — and the
 * natural response is to add the row again, so a compliance matrix ends up with
 * duplicates. So: always confirm, and when the row landed outside the current
 * view, say that rather than claiming it is on screen.
 *
 * The filters are never cleared on the user's behalf. They were set
 * deliberately (often by a nudge link), so throwing them away silently is its
 * own surprise — the toast offers the action instead.
 */

export interface AddedItemToastOptions {
  /** Sentence-cased singular noun for the row, e.g. 'Requirement'. */
  label: string
  /** True when the saved row is not visible under the table's current narrowing. */
  hidden: boolean
  /** Restores visibility. Wired to the toast action; ignored unless `hidden`. */
  onShowItem?: (() => void) | undefined
}

/** How long the "it is hidden" toast stays up — long enough to act on. */
const HIDDEN_TOAST_DURATION_MS = 10_000

export function toastItemAdded({ label, hidden, onShowItem }: AddedItemToastOptions): void {
  if (!hidden || !onShowItem) {
    toast.success(`${label} added`)
    return
  }
  toast.success(`${label} added, but hidden by your current filters`, {
    description: 'It is saved. Clear the filters to see it.',
    action: { label: 'Clear filters', onClick: onShowItem },
    duration: HIDDEN_TOAST_DURATION_MS,
  })
}

/** Failure counterpart — silence on a failed save reads exactly like silence on a good one. */
export function toastItemAddFailed(label: string): void {
  toast.error(`Could not add ${label.toLowerCase()}. Please try again.`)
}

/**
 * How a table decides whether the row it just created is on screen, and how to
 * put it there. `null` for a table with no narrowing controls at all.
 */
export interface AddVisibility<T> {
  isVisible: (created: T) => boolean
  onShowItem: () => void
}

/**
 * Run a manual add and report the outcome — the only path the Analysis tables
 * should use. Rethrows on failure so the add dialog stays open with the typed
 * values.
 */
export async function addItemWithFeedback<T>(
  label: string,
  add: () => Promise<T>,
  visibility: AddVisibility<T> | null
): Promise<void> {
  let created: T
  try {
    created = await add()
  } catch (error) {
    toastItemAddFailed(label)
    throw error
  }
  toastItemAdded({
    label,
    hidden: visibility !== null && !isVisibleOrAssumeVisible(visibility, created),
    onShowItem: visibility?.onShowItem,
  })
}

/**
 * The row is already saved by the time the predicate runs, so a throw here must
 * never cost the user their confirmation: that is the exact silence this module
 * exists to remove, except now the row IS persisted, so re-adding duplicates it.
 * Degrade to the plain "added" toast — a slightly-wrong confirmation beats none.
 */
function isVisibleOrAssumeVisible<T>(visibility: AddVisibility<T>, created: T): boolean {
  try {
    return visibility.isVisible(created)
  } catch (error) {
    console.error('Could not test the new row against the active filters:', error)
    return true
  }
}
